import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { AppState } from '@store/app.state';
import { FrameActions } from '@store/actions/frame.actions';
import { selectAllFrames, selectCurrentFrameId, selectLayers, selectCels, selectActiveLayerId } from '@store/selectors/frame.selectors';
import { selectSettings } from '@store/selectors/settings.selector';
import { SettingsActions } from '@store/actions/settings.actions';
import { PixelFrame, createBlankFrame } from '@models/frame.model';
import { SpriteLayer, createBlankSpriteLayer } from '@models/layer.model';
import {
  Settings,
  CANVAS_DIM_MAX,
  CANVAS_DIM_MIN,
  ScreenResolution,
  clampCanvasDimension,
  defaultSettings,
  isCustomResolutionPreset,
  isSameNewProjectResolutionCard,
  newProjectResolutionPresets,
  resolutionFromCustomSize,
} from '@models/settings.model';
import { PersistenceService, ProjectEntry } from '@app/services/persistence.service';
import { AuthService } from '@app/services/auth.service';
import { CollaborationService } from '@app/services/collaboration.service';
import { generateUUID } from '@app/lib/utility/helper';
import { PxlNumberStepperDirective } from '@app/shared/pxl-number-stepper/pxl-number-stepper.directive';
import { Output as MediaOutput, Mp4OutputFormat, WebMOutputFormat, MkvOutputFormat, MovOutputFormat, BufferTarget, CanvasSource, QUALITY_HIGH } from 'mediabunny';
import JSZip from 'jszip';
import GIFEncoder, { quantize, applyPalette, prequantize } from 'gifenc';

type ExportFormat = 'gif' | 'spritesheet' | 'video' | 'image' | 'pxlvd' | null;
type VideoFormat = 'webm' | 'mp4' | 'mov' | 'mkv';
type ImageFormat = 'png' | 'webp' | 'jpg' | 'svg';
type VideoStatusTone = 'info' | 'warning' | 'error' | 'success';

@Component({
  selector: 'app-file-menu',
  standalone: true,
  imports: [CommonModule, AsyncPipe, FormsModule, PxlNumberStepperDirective],
  templateUrl: './file-menu.html',
  styleUrls: ['./file-menu.css']
})
export class FileMenu implements OnInit, OnDestroy {
  isOpen = false;
  showExportModal: ExportFormat = null;
  exportProgress = 0;
  isExporting = false;
  showVideoPreview = false;
  previewVideoUrl: string | null = null;
  previewFileName = '';
  previewCurrentTime = 0;
  previewDuration = 0;
  previewIsPlaying = false;
  previewIsFullscreen = false;
  previewHasAudio = false;
  previewMuted = true;
  previewLoop = true;
  previewVolume = 1;
  videoStatusMessage = '';
  videoStatusTone: VideoStatusTone = 'info';
  imageStatusMessage = '';
  imageStatusTone: VideoStatusTone = 'info';
  gifStatusMessage = '';
  gifStatusTone: VideoStatusTone = 'info';
  readonly videoExportWidths = [720, 1080, 1920, 2048, 3840, 7680];

  // Export configs
  gifFps = 12; gifScale = 4; gifDither = false;
  gifTransparentBackground = true;
  spriteColumns = 4; spriteScale = 4;
  imageFormat: ImageFormat = 'png';
  imageScale = 4;
  imageExportAllFrames = false;
  imageTransparentBackground = true;
  imageBackgroundColor = '#000000';
  imageQuality = 92;
  videoFormat: VideoFormat = 'mp4';
  videoFps = 24;
  videoWidth = 1080;
  @Output() authRequired = new EventEmitter<void>();
  @Output() paymentRequired = new EventEmitter<{ feature: 'export'; proceed: () => void }>();
  /** Emitted when a .pxlvd file is successfully imported via the file menu. */
  @Output() fileImported = new EventEmitter<void>();
  /** Emitted when the user clicks Save from the file menu - handled by app.ts */
  @Output() saveRequested = new EventEmitter<void>();
  /** Emitted when the menu opens so the host can close any overlapping panels. */
  @Output() menuOpened = new EventEmitter<void>();
  /** Emitted to ask the host to open the startup / Projects view. */
  @Output() openProjectManagerRequested = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  private frames: PixelFrame[] = [];
  private layers: SpriteLayer[] = [];
  private cels: Record<string, string[][]> = {};
  private activeLayerId: string | null = null;
  private settings!: Settings;
  private currentFrameId: string | null = null;
  private readonly onFullscreenChange = () => {
    const doc = document as Document & { webkitFullscreenElement?: Element };
    this.previewIsFullscreen = Boolean(doc.fullscreenElement || doc.webkitFullscreenElement);
  };

  constructor(
    private store: Store<AppState>,
    private persistence: PersistenceService,
    private auth: AuthService,
    public collabSvc: CollaborationService,
  ) {
  }

  ngOnInit(): void {
    this.store.select(selectAllFrames).pipe(takeUntil(this.destroy$)).subscribe(f => this.frames = f);
    this.store.select(selectLayers).pipe(takeUntil(this.destroy$)).subscribe(l => this.layers = l);
    this.store.select(selectCels).pipe(takeUntil(this.destroy$)).subscribe(c => this.cels = c);
    this.store.select(selectActiveLayerId).pipe(takeUntil(this.destroy$)).subscribe(id => this.activeLayerId = id);
    this.store.select(selectSettings).pipe(takeUntil(this.destroy$)).subscribe(s => this.settings = s);
    this.store.select(selectCurrentFrameId).pipe(takeUntil(this.destroy$)).subscribe(id => this.currentFrameId = id);
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange as EventListener);
  }
  ngOnDestroy(): void {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange as EventListener);
    if (this.previewIsFullscreen) void this.exitPreviewFullscreen();
    this.clearPreviewUrl();
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggle(): void {
    if (this.collabSvc.role$.value === 'spectator') return;
    if (!this.auth.isAuthenticated) {
      this.authRequired.emit();
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) this.menuOpened.emit();
  }
  close(): void { this.isOpen = false; }
  get isSpectator(): boolean { return this.collabSvc.role$.value === 'spectator'; }

  // ── New Project modal ──
  showNewModal = false;
  newProjectName = 'Untitled Project';
  newProjectRes = defaultSettings[1]; // default 54×96
  newProjectUseCustom = false;
  customW = 64;
  customH = 64;
  resPresets = newProjectResolutionPresets;
  readonly CANVAS_DIM_MIN = CANVAS_DIM_MIN;
  readonly CANVAS_DIM_MAX = CANVAS_DIM_MAX;

  openNewProject(): void {
    this.newProjectName = 'Untitled Project';
    this.newProjectRes = defaultSettings[1];
    this.newProjectUseCustom = false;
    this.customW = 64;
    this.customH = 64;
    this.showNewModal = true;
    this.isOpen = false;
  }

  selectNewProjectRes(r: ScreenResolution): void {
    if (isCustomResolutionPreset(r)) {
      this.newProjectUseCustom = true;
      this.newProjectRes = resolutionFromCustomSize(this.customW, this.customH);
    } else {
      this.newProjectUseCustom = false;
      this.newProjectRes = r;
    }
  }

  isNewProjectResActive(r: ScreenResolution): boolean {
    const selected = this.newProjectUseCustom
      ? resolutionFromCustomSize(this.customW, this.customH)
      : this.newProjectRes;
    return isSameNewProjectResolutionCard(selected, r);
  }

  newProjectPreviewDims(r: ScreenResolution): { w: number; h: number } {
    if (r.label === 'Custom') {
      return { w: clampCanvasDimension(this.customW), h: clampCanvasDimension(this.customH) };
    }
    return { w: r.width, h: r.height };
  }

  newProjectPreviewAspect(r: ScreenResolution): string {
    const d = this.newProjectPreviewDims(r);
    return `${d.w}/${d.h}`;
  }

  newProjectPreviewTall(r: ScreenResolution): boolean {
    const d = this.newProjectPreviewDims(r);
    return d.h > d.w;
  }

  newProjectDimLabel(r: ScreenResolution): string {
    if (r.label === 'Custom') {
      return `${clampCanvasDimension(this.customW)}×${clampCanvasDimension(this.customH)}`;
    }
    return `${r.width}×${r.height}`;
  }

  effectiveNewProjectResolution(): ScreenResolution {
    return this.newProjectUseCustom
      ? resolutionFromCustomSize(this.customW, this.customH)
      : this.newProjectRes;
  }

  async confirmNewProject(): Promise<void> {
    const name = this.newProjectName.trim() || 'Untitled Project';
    const res = this.effectiveNewProjectResolution();
    if (res.width < CANVAS_DIM_MIN || res.height < CANVAS_DIM_MIN || res.width > CANVAS_DIM_MAX || res.height > CANVAS_DIM_MAX) {
      alert(`Canvas size must be between ${CANVAS_DIM_MIN} and ${CANVAS_DIM_MAX} pixels.`);
      return;
    }
    // Check for duplicate name
    const existing = await this.persistence.listProjects();
    const duplicate = existing.find(p => p.name.toLowerCase() === name.toLowerCase());
    if (duplicate) {
      const proceed = confirm(`A project named "${name}" already exists. Create anyway with a different name?`);
      if (!proceed) return;
      // Append timestamp to make unique
      this.newProjectName = `${name} (${new Date().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})})`;
      return; // let user see the updated name before confirming
    }
    if (!confirm('Start a new project? Unsaved work will be lost.')) return;
    const { width, height } = res;
    const projectId = generateUUID();
    const finalName = this.newProjectName.trim() || 'Untitled Project';
    const blankFrame = createBlankFrame(width, height);
    // Bug-fix: always pass a fresh default layer so that layer names from the
    // previously-loaded project don't bleed into the new one.
    // setFrames falls through to state.layers when layers is omitted.
    const defaultLayer = createBlankSpriteLayer('Layer 1');
    this.store.dispatch(FrameActions.setFrames({ frames: [blankFrame], layers: [defaultLayer], cels: {}, activeLayerId: defaultLayer.id }));
    this.store.dispatch(SettingsActions.updateSettings({ settings: { projectName: finalName, currentProjectId: projectId, Resolution: res } as any }));
    // Save immediately to IDB so it appears in recent projects
    await this.persistence.saveProject(projectId, finalName, [blankFrame], blankFrame.id, [defaultLayer], {}, defaultLayer.id);
    localStorage.setItem('pxlvd-last-project', projectId);
    this.showNewModal = false;
  }

  // ── Recent Projects modal ──
  showRecentModal = false;
  recentProjects: ProjectEntry[] = [];
  recentLoading = false;
  renamingId: string | null = null;
  renameValue = '';

  async openRecentModal(): Promise<void> {
    this.recentLoading = true;
    this.showRecentModal = true;
    this.isOpen = false;
    this.recentProjects = await this.persistence.listProjects();
    this.recentLoading = false;
  }

  async loadRecentProject(id: string, name: string): Promise<void> {
    // Loading a local project while in a collab room would overwrite the shared canvas
    // for all collaborators.  Block it entirely and direct the user to leave first.
    if (this.collabSvc.isInRoom$.value) {
      alert('You cannot load a project while in a collaboration room.\nLeave the room first, then load your project.');
      return;
    }
    await this.doLoadProject(id, name);
  }

  /** Perform the actual project load. */
  async doLoadProject(id: string, name: string): Promise<void> {
    const data = await this.persistence.loadProject(id);
    if (data?.frames?.length) {
      const migrated = this.persistence.migrateFrameData(data);
      this.store.dispatch(FrameActions.restoreFrameState({
        frames: migrated.frames,
        layers: migrated.layers,
        cels: migrated.cels,
        activeLayerId: migrated.activeLayerId,
        currentFrameId: migrated.currentFrameId,
        past: migrated.past,
        future: migrated.future,
      }));
      // Restore canvas resolution from frame pixel dimensions.
      // Canvas size is fixed at project creation time and must match the frames.
      const firstFrame = migrated.frames[0];
      const w = firstFrame?.pixels?.[0]?.length ?? 54;
      const h = firstFrame?.pixels?.length ?? 96;
      this.store.dispatch(SettingsActions.updateSettings({
        settings: { projectName: name, currentProjectId: id, Resolution: resolutionFromCustomSize(w, h) } as any
      }));
      await this.persistence.touchProject(id);
      localStorage.setItem('pxlvd-last-project', id);
      this.showRecentModal = false;
    } else {
      alert('Could not load project - data may be missing.');
    }
  }

  startRename(p: ProjectEntry): void {
    this.renamingId = p.id;
    this.renameValue = p.name;
    setTimeout(() => (document.querySelector('.recent-rename-input') as HTMLInputElement)?.focus(), 30);
  }

  async commitRename(p: ProjectEntry): Promise<void> {
    const newName = this.renameValue.trim();
    if (!newName || newName === p.name) { this.renamingId = null; return; }
    // Check for duplicate name
    const duplicate = this.recentProjects.find(x => x.id !== p.id && x.name.toLowerCase() === newName.toLowerCase());
    if (duplicate) { alert(`A project named "${newName}" already exists.`); return; }
    const data = await this.persistence.loadProject(p.id);
    if (data) await this.persistence.saveProject(p.id, newName, data.frames, data.currentFrameId);
    // Update settings if this is the active project
    this.store.select(selectSettings).pipe(take(1)).subscribe(s => {
      if (s.currentProjectId === p.id) {
        this.store.dispatch(SettingsActions.updateSettings({ settings: { projectName: newName } as any }));
      }
    });
    this.renamingId = null;
    this.recentProjects = await this.persistence.listProjects();
  }

  async deleteProject(p: ProjectEntry): Promise<void> {
    const isActive = this.settings?.currentProjectId === p.id;
    const warning  = isActive
      ? `"${p.name}" is your current project. Delete it? This cannot be undone.`
      : `Delete "${p.name}"? This cannot be undone.`;
    if (!confirm(warning)) return;

    await this.persistence.deleteProject(p.id);

    // Clear local last-project pointer if we just removed it
    const lastId = localStorage.getItem('pxlvd-last-project');
    if (lastId === p.id) localStorage.removeItem('pxlvd-last-project');

    if (isActive) {
      this.store.dispatch(SettingsActions.updateSettings({
        settings: { currentProjectId: undefined } as any
      }));
    }

    this.recentProjects = await this.persistence.listProjects();

    // If the list is now empty, close this modal and go to the Projects view
    if (this.recentProjects.length === 0) {
      this.showRecentModal = false;
      this.openProjectManagerRequested.emit();
    }
  }

  importProject(e: Event): void {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string);
        if (data.version && data.frames) {
          // Run the migration helper so old v1 files (no layers/cels) still load correctly
          const migrated = this.persistence.migrateFrameData({
            frames:        data.frames,
            layers:        data.layers        ?? undefined,
            cels:          data.cels          ?? undefined,
            activeLayerId: data.activeLayerId ?? undefined,
            currentFrameId: data.currentFrameId ?? null,
            past:   [],
            future: [],
          });

          const first = migrated.frames[0];
          const importedWidth  = first?.pixels?.[0]?.length ?? this.settings?.Resolution?.width  ?? 54;
          const importedHeight = first?.pixels?.length       ?? this.settings?.Resolution?.height ?? 96;
          const projectName = (data.name?.trim?.() || 'Imported Project') as string;
          // Re-use an existing project with the same name instead of creating a duplicate
          const existingProjects = await this.persistence.listProjects();
          const nameMatch = existingProjects.find(p => p.name.toLowerCase() === projectName.toLowerCase());
          const projectId = nameMatch?.id ?? generateUUID();
          const resolution  = {
            ...(this.settings?.Resolution ?? defaultSettings[1]),
            width:        importedWidth,
            height:       importedHeight,
            exportWidth:  importedWidth  * 20,
            exportHeight: importedHeight * 20,
          };

          // Restore the full frame state (frames + layers + cels) in one shot
          this.store.dispatch(FrameActions.restoreFrameState({
            frames:        migrated.frames,
            layers:        migrated.layers,
            cels:          migrated.cels,
            activeLayerId: migrated.activeLayerId,
            currentFrameId: migrated.currentFrameId ?? first?.id ?? null,
            past:   [],
            future: [],
          }));
          this.store.dispatch(SettingsActions.updateSettings({
            settings: { projectName, currentProjectId: projectId, Resolution: resolution } as any
          }));

          await this.persistence.saveProject(
            projectId, projectName,
            migrated.frames, migrated.currentFrameId ?? first?.id ?? null,
            migrated.layers, migrated.cels, migrated.activeLayerId,
          );
          // Keep legacy frame snapshot in sync for refresh recovery
          await this.persistence.saveFrames({
            frames:        migrated.frames,
            currentFrameId: migrated.currentFrameId ?? first?.id ?? null,
            layers:        migrated.layers,
            cels:          migrated.cels,
            activeLayerId: migrated.activeLayerId,
            past:   [],
            future: [],
          });
          await this.persistence.touchProject(projectId);
          localStorage.setItem('pxlvd-last-project', projectId);
          this.fileImported.emit();
          alert(`Project "${projectName}" loaded - ${migrated.frames.length} frames.`);
        } else alert('Invalid .pxlvd file');
      } catch { alert('Could not parse file'); }
    };
    reader.readAsText(file);
    (e.target as HTMLInputElement).value = '';
    this.close();
  }

  exportPxlvd(): void {
    if (!this.auth.isAuthenticated) {
      this.authRequired.emit();
      return;
    }
    this.close();
    this._requestExportPaidAccess(() => this._exportPxlvdNow());
  }

  private _exportPxlvdNow(): void {
    const projectName = this.settings?.projectName?.trim() || 'Untitled Project';
    const data = {
      version: 2,
      name: projectName,
      frames: this.frames,
      layers: this.layers,
      cels: this.cels,
      activeLayerId: this.activeLayerId,
      currentFrameId: this.currentFrameId,
      settings: this.settings,
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    this.downloadBlob(blob, `${this.getProjectFilenameBase()}.pxlvd`);
  }


  openExport(fmt: ExportFormat): void {
    if (!this.auth.isAuthenticated) {
      this.authRequired.emit();
      return;
    }
    this.close();
    this._requestExportPaidAccess(() => {
      this.showExportModal = fmt;
      if (fmt === 'video') this.clearVideoStatus();
      if (fmt === 'image') this.clearImageStatus();
      if (fmt === 'gif') this.clearGifStatus();
    });
  }

  private _requestExportPaidAccess(proceed: () => void): void {
    this.paymentRequired.emit({ feature: 'export', proceed });
  }

  // ── Export implementations ──
  async exportSpritesheet(): Promise<void> {
    const { width, height } = this.settings.Resolution;
    const scale = this.spriteScale;
    const cols = Math.min(this.spriteColumns, this.frames.length);
    const rows = Math.ceil(this.frames.length / cols);
    const canvas = document.createElement('canvas');
    canvas.width = cols * width * scale;
    canvas.height = rows * height * scale;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    this.frames.forEach((frame, i) => {
      const col = i % cols, row = Math.floor(i / cols);
      for (let r = 0; r < height; r++) for (let c = 0; c < width; c++) {
        const px = frame.pixels[r]?.[c];
        if (!px || px === '#00000000' || px === 'transparent') continue;
        ctx.fillStyle = px;
        ctx.fillRect(col*width*scale + c*scale, row*height*scale + r*scale, scale, scale);
      }
    });
    const baseName = this.getProjectFilenameBase();
    canvas.toBlob(blob => { if (blob) this.downloadBlob(blob, `${baseName}_spritesheet.png`); });
    this.showExportModal = null;
  }

  getImageFormats(): ImageFormat[] {
    return ['png', 'webp', 'jpg', 'svg'];
  }

  async exportImage(): Promise<void> {
    this.clearImageStatus();
    const frames = this.getSelectedFramesForImageExport();
    if (!frames.length) {
      this.setImageStatus('error', 'No frame available for image export.');
      return;
    }

    this.isExporting = true;
    this.exportProgress = 0;
    const baseName = this.getProjectFilenameBase();
    const format = this.imageFormat;
    try {
      if (!this.imageExportAllFrames) {
        const blob = await this.renderImageBlob(frames[0], format);
        if (!blob) {
          this.setImageStatus('error', `${format.toUpperCase()} export failed.`);
          return;
        }
        this.downloadBlob(blob, `${baseName}.${this.getImageExtension(format)}`);
        this.setImageStatus('success', `${format.toUpperCase()} export finished.`);
      } else {
        const zip = new JSZip();
        for (let i = 0; i < frames.length; i++) {
          const blob = await this.renderImageBlob(frames[i], format);
          if (!blob) {
            this.setImageStatus('error', `${format.toUpperCase()} export failed on frame ${i + 1}.`);
            return;
          }
          const name = `${baseName}_frame_${String(i + 1).padStart(3, '0')}.${this.getImageExtension(format)}`;
          zip.file(name, blob);
          this.exportProgress = Math.round(((i + 1) / frames.length) * 90);
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' }, meta => {
          this.exportProgress = 90 + Math.round(meta.percent * 0.1);
        });
        this.downloadBlob(zipBlob, `${baseName}_${format}_frames.zip`);
        this.setImageStatus('success', `${frames.length} ${format.toUpperCase()} frames exported.`);
      }
      this.showExportModal = null;
    } catch (error) {
      console.error('[image-export] Export failed', error);
      this.setImageStatus('error', 'Image export failed.');
    } finally {
      this.isExporting = false;
      this.exportProgress = 0;
    }
  }

  isImageBackgroundColorVisible(): boolean {
    if (this.imageFormat === 'jpg') return true;
    return !this.imageTransparentBackground;
  }

  private getSelectedFramesForImageExport(): PixelFrame[] {
    if (this.imageExportAllFrames) return [...this.frames];
    if (!this.currentFrameId) return this.frames.length ? [this.frames[0]] : [];
    const current = this.frames.find(f => f.id === this.currentFrameId);
    return current ? [current] : (this.frames.length ? [this.frames[0]] : []);
  }

  private async renderImageBlob(frame: PixelFrame, format: ImageFormat): Promise<Blob | null> {
    if (format === 'svg') {
      const svgText = this.renderFrameAsSvg(frame, this.imageScale, this.getImageMatteColor(format));
      return new Blob([svgText], { type: 'image/svg+xml' });
    }

    const canvas = this.renderFrameToCanvas(frame, this.imageScale, this.getImageMatteColor(format));
    const mimeType = this.getMimeTypeForImageFormat(format);
    const quality = format === 'jpg' || format === 'webp' ? this.imageQuality / 100 : undefined;
    return await new Promise<Blob | null>(resolve => canvas.toBlob(b => resolve(b), mimeType, quality));
  }

  private renderFrameToCanvas(frame: PixelFrame, scale: number, matteColor: string | null): HTMLCanvasElement {
    const srcH = frame.pixels.length;
    const srcW = frame.pixels[0]?.length ?? 0;
    const pixelScale = Math.max(1, Math.round(scale));
    const canvas = document.createElement('canvas');
    canvas.width = srcW * pixelScale;
    canvas.height = srcH * pixelScale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;
    ctx.imageSmoothingEnabled = false;

    if (matteColor) {
      ctx.fillStyle = matteColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    for (let r = 0; r < srcH; r++) {
      for (let c = 0; c < srcW; c++) {
        const color = this.normalizePixelColor(frame.pixels[r]?.[c]);
        if (!color) continue;
        ctx.fillStyle = color;
        ctx.fillRect(c * pixelScale, r * pixelScale, pixelScale, pixelScale);
      }
    }
    return canvas;
  }

  private renderFrameAsSvg(frame: PixelFrame, scale: number, matteColor: string | null): string {
    const srcH = frame.pixels.length;
    const srcW = frame.pixels[0]?.length ?? 0;
    const pixelScale = Math.max(1, Math.round(scale));
    const outW = srcW * pixelScale;
    const outH = srcH * pixelScale;
    const rects: string[] = [];
    if (matteColor) rects.push(`<rect width="${outW}" height="${outH}" fill="${matteColor}" />`);

    for (let r = 0; r < srcH; r++) {
      for (let c = 0; c < srcW; c++) {
        const rgba = this.parsePixelColor(frame.pixels[r]?.[c]);
        if (!rgba) continue;
        const fill = `rgb(${rgba.r},${rgba.g},${rgba.b})`;
        const opacityAttr = rgba.a < 1 ? ` fill-opacity="${rgba.a.toFixed(3)}"` : '';
        rects.push(`<rect x="${c * pixelScale}" y="${r * pixelScale}" width="${pixelScale}" height="${pixelScale}" fill="${fill}"${opacityAttr} />`);
      }
    }

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${outW}" height="${outH}" viewBox="0 0 ${outW} ${outH}" shape-rendering="crispEdges">
${rects.join('')}
</svg>`;
  }

  async exportVideo(): Promise<void> {
    this.clearVideoStatus();
    const blob = await this.renderVideoBlob(this.videoFormat);
    if (!blob) return;
    const baseName = this.getProjectFilenameBase();
    this.downloadAndPreviewVideo(blob, `${baseName}.${this.videoFormat}`);
    this.setVideoStatus('success', `${this.videoFormat.toUpperCase()} export finished.`);
    this.showExportModal = null;
  }

  async previewVideo(): Promise<void> {
    this.clearVideoStatus();
    const blob = await this.renderVideoBlob(this.videoFormat);
    if (blob) {
      const baseName = this.getProjectFilenameBase();
      this.openVideoPreview(blob, `${baseName}.${this.videoFormat}`);
      this.setVideoStatus('success', `Preview ready (${this.videoFormat.toUpperCase()}).`);
      return;
    }
    this.setVideoStatus('error', `Preview failed for ${this.videoFormat.toUpperCase()}.`);
  }

  async exportGif(): Promise<void> {
    this.clearGifStatus();
    const frames = this.frames;
    if (!frames.length) {
      this.setGifStatus('error', 'No frames to export.');
      return;
    }

    this.isExporting = true;
    this.exportProgress = 0;

    try {
      const scale = Math.max(1, Math.round(Number(this.gifScale)));
      const fps = Math.min(60, Math.max(1, Math.round(Number(this.gifFps))));
      const delayMs = Math.max(20, Math.round(1000 / fps));
      const gif = GIFEncoder();
      const n = frames.length;
      const matte = this.gifTransparentBackground ? null : this.imageBackgroundColor;

      for (let fi = 0; fi < n; fi++) {
        const canvas = this.renderFrameToCanvas(frames[fi], scale, matte);
        const w = canvas.width;
        const h = canvas.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          this.setGifStatus('error', 'Could not read canvas for GIF export.');
          return;
        }
        const imageData = ctx.getImageData(0, 0, w, h);
        const rgba = imageData.data;

        if (this.gifDither) {
          prequantize(rgba, {
            roundRGB: 5,
            roundAlpha: 10,
            oneBitAlpha: this.gifTransparentBackground ? 127 : null,
          });
        }

        let palette: number[][];
        let index: Uint8Array;
        if (this.gifTransparentBackground) {
          palette = quantize(rgba, 256, {
            format: 'rgba4444',
            oneBitAlpha: true,
            clearAlpha: true,
            clearAlphaThreshold: 0,
          });
          index = applyPalette(rgba, palette, 'rgba4444');
        } else {
          palette = quantize(rgba, 256, { format: 'rgb565' });
          index = applyPalette(rgba, palette, 'rgb565');
        }

        const transparentIndex = this.gifTransparentBackground
          ? this.findTransparentPaletteIndex(palette)
          : -1;

        gif.writeFrame(index, w, h, {
          palette,
          delay: delayMs,
          repeat: fi === 0 ? 0 : undefined,
          transparent: this.gifTransparentBackground && transparentIndex >= 0,
          transparentIndex: transparentIndex >= 0 ? transparentIndex : 0,
        });

        this.exportProgress = Math.round(((fi + 1) / n) * 100);
        await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      }

      gif.finish();
      const bytes = gif.bytes();
      const blob = new Blob([new Uint8Array(bytes)], { type: 'image/gif' });
      const baseName = this.getProjectFilenameBase();
      this.downloadBlob(blob, `${baseName}.gif`);
      this.setGifStatus('success', 'GIF export finished.');
      this.showExportModal = null;
    } catch (error) {
      console.error('[gif-export] Export failed', error);
      this.setGifStatus('error', 'GIF export failed.');
    } finally {
      this.isExporting = false;
      this.exportProgress = 0;
    }
  }

  async exportPngSequence(): Promise<void> {
    const scale = Math.max(1, Math.round(Number(this.gifScale)));
    const baseName = this.getProjectFilenameBase();
    for (let i = 0; i < this.frames.length; i++) {
      const canvas = this.renderFrameToCanvas(this.frames[i], scale, null);
      const blob = await new Promise<Blob | null>(resolve =>
        canvas.toBlob(b => resolve(b), 'image/png'),
      );
      if (blob) this.downloadBlob(blob, `${baseName}_frame_${String(i + 1).padStart(3, '0')}.png`);
      await new Promise<void>(resolve => setTimeout(() => resolve(), 30));
    }
    this.showExportModal = null;
  }

  private downloadBlob(blob: Blob, name: string): void {
    const url = URL.createObjectURL(blob);
    this.triggerDownload(url, name);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  closeVideoPreview(): void {
    if (this.previewIsFullscreen) void this.exitPreviewFullscreen();
    this.showVideoPreview = false;
    this.previewFileName = '';
    this.previewCurrentTime = 0;
    this.previewDuration = 0;
    this.previewIsPlaying = false;
    this.previewHasAudio = false;
    this.clearPreviewUrl();
  }

  downloadPreviewVideo(): void {
    if (!this.previewVideoUrl || !this.previewFileName) return;
    this.triggerDownload(this.previewVideoUrl, this.previewFileName);
  }

  getVideoResolutionLabel(width: number): string {
    if (width === 2048) return '2K';
    if (width === 3840) return '4K';
    if (width === 7680) return '8K';
    return `${width}p`;
  }

  private getVideoSizeForWidth(targetWidth: number): { width: number; height: number } {
    const { width, height } = this.settings.Resolution;
    const aspect = height / width;
    const outputWidth = Math.max(1, Math.round(targetWidth));
    // Keep even dimensions for encoder compatibility.
    const outputHeight = Math.max(2, 2 * Math.round((outputWidth * aspect) / 2));
    return { width: outputWidth, height: outputHeight };
  }

  private triggerDownload(url: string, name: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
  }

  private downloadAndPreviewVideo(blob: Blob, name: string): void {
    const downloadUrl = URL.createObjectURL(blob);
    this.triggerDownload(downloadUrl, name);
    setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);

    this.openVideoPreview(blob, name);
  }

  private clearPreviewUrl(): void {
    if (!this.previewVideoUrl) return;
    URL.revokeObjectURL(this.previewVideoUrl);
    this.previewVideoUrl = null;
  }

  private openVideoPreview(blob: Blob, name: string): void {
    this.clearPreviewUrl();
    this.previewVideoUrl = URL.createObjectURL(blob);
    this.previewFileName = name;
    this.previewCurrentTime = 0;
    this.previewDuration = 0;
    this.previewIsPlaying = false;
    this.previewIsFullscreen = false;
    this.previewHasAudio = false;
    this.previewMuted = true;
    this.previewVolume = 1;
    this.showVideoPreview = true;
  }

  onPreviewLoadedMetadata(video: HTMLVideoElement): void {
    this.previewDuration = Number.isFinite(video.duration) ? video.duration : 0;
    this.updatePreviewAudioAvailability(video);
  }

  onPreviewTimeUpdate(video: HTMLVideoElement): void {
    this.previewCurrentTime = video.currentTime;
    if (!this.previewHasAudio) this.updatePreviewAudioAvailability(video);
  }

  onPreviewPlayState(isPlaying: boolean): void {
    this.previewIsPlaying = isPlaying;
  }

  async togglePreviewPlayback(video: HTMLVideoElement): Promise<void> {
    if (video.paused) {
      try {
        await video.play();
      } catch {
        this.previewIsPlaying = false;
      }
      return;
    }
    video.pause();
  }

  seekPreview(timeValue: string, video: HTMLVideoElement): void {
    const nextTime = Number(timeValue);
    if (!Number.isFinite(nextTime)) return;
    video.currentTime = Math.max(0, Math.min(this.previewDuration || 0, nextTime));
    this.previewCurrentTime = video.currentTime;
  }

  togglePreviewMuted(video: HTMLVideoElement): void {
    if (!this.previewHasAudio) return;
    this.previewMuted = !this.previewMuted;
    video.muted = this.previewMuted;
  }

  setPreviewVolume(volumeValue: string, video: HTMLVideoElement): void {
    if (!this.previewHasAudio) return;
    const nextVolume = Number(volumeValue);
    if (!Number.isFinite(nextVolume)) return;
    this.previewVolume = Math.max(0, Math.min(1, nextVolume));
    video.volume = this.previewVolume;
    if (this.previewVolume > 0 && this.previewMuted) {
      this.previewMuted = false;
      video.muted = false;
    }
    if (this.previewVolume === 0) {
      this.previewMuted = true;
      video.muted = true;
    }
  }

  togglePreviewLoop(video: HTMLVideoElement): void {
    this.previewLoop = !this.previewLoop;
    video.loop = this.previewLoop;
  }

  async togglePreviewFullscreen(video: HTMLVideoElement): Promise<void> {
    const doc = document as Document & {
      webkitFullscreenElement?: Element;
      webkitExitFullscreen?: () => Promise<void> | void;
    };
    const activeFullscreenElement = doc.fullscreenElement || doc.webkitFullscreenElement;
    if (activeFullscreenElement) {
      await this.exitPreviewFullscreen();
      return;
    }
    const fsVideo = video as HTMLVideoElement & { webkitRequestFullscreen?: () => Promise<void> | void };
    if (video.requestFullscreen) {
      await video.requestFullscreen();
      this.previewIsFullscreen = true;
      return;
    }
    if (fsVideo.webkitRequestFullscreen) {
      await fsVideo.webkitRequestFullscreen();
      this.previewIsFullscreen = true;
    }
  }

  formatPreviewTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const total = Math.floor(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  }

  private getProjectFilenameBase(): string {
    const rawName = this.settings?.projectName?.trim() || 'project';
    const withoutInvalidChars = rawName.replace(/[<>:"/\\|?*\x00-\x1F]/g, '_');
    const withoutTrailingDotsOrSpaces = withoutInvalidChars.replace(/[. ]+$/g, '');
    const normalized = withoutTrailingDotsOrSpaces.replace(/\s+/g, ' ').trim();
    return normalized || 'project';
  }

  private getImageMatteColor(format: ImageFormat): string | null {
    if (format === 'jpg') return this.imageBackgroundColor;
    if (this.imageTransparentBackground) return null;
    return this.imageBackgroundColor;
  }

  private getImageExtension(format: ImageFormat): string {
    return format === 'jpg' ? 'jpg' : format;
  }

  private getMimeTypeForImageFormat(format: ImageFormat): string {
    if (format === 'png') return 'image/png';
    if (format === 'webp') return 'image/webp';
    if (format === 'jpg') return 'image/jpeg';
    return 'image/svg+xml';
  }

  private parsePixelColor(color: string | undefined): { r: number; g: number; b: number; a: number } | null {
    if (!color || color === 'transparent' || color === '#00000000') return null;
    if (!color.startsWith('#')) return null;
    const hex = color.slice(1);
    if (hex.length !== 6 && hex.length !== 8) return null;
    const r = Number.parseInt(hex.slice(0, 2), 16);
    const g = Number.parseInt(hex.slice(2, 4), 16);
    const b = Number.parseInt(hex.slice(4, 6), 16);
    const a = hex.length === 8 ? Number.parseInt(hex.slice(6, 8), 16) / 255 : 1;
    if ([r, g, b, a].some(v => Number.isNaN(v))) return null;
    return { r, g, b, a };
  }

  private normalizePixelColor(color: string | undefined): string | null {
    const rgba = this.parsePixelColor(color);
    if (!rgba) return null;
    if (rgba.a >= 1) return `rgb(${rgba.r}, ${rgba.g}, ${rgba.b})`;
    return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
  }

  private updatePreviewAudioAvailability(video: HTMLVideoElement): void {
    const candidate = video as HTMLVideoElement & {
      audioTracks?: { length: number };
      mozHasAudio?: boolean;
      webkitAudioDecodedByteCount?: number;
      captureStream?: () => MediaStream;
    };

    const audioTrackCount = candidate.audioTracks?.length ?? 0;
    const hasMozAudio = candidate.mozHasAudio === true;
    const hasDecodedWebkitAudio = (candidate.webkitAudioDecodedByteCount ?? 0) > 0;
    let hasCaptureAudio = false;

    if (!audioTrackCount && !hasMozAudio && !hasDecodedWebkitAudio && candidate.captureStream) {
      try {
        const stream = candidate.captureStream();
        hasCaptureAudio = stream.getAudioTracks().length > 0;
        stream.getTracks().forEach(track => track.stop());
      } catch {
        hasCaptureAudio = false;
      }
    }

    this.previewHasAudio = audioTrackCount > 0 || hasMozAudio || hasDecodedWebkitAudio || hasCaptureAudio;
    if (!this.previewHasAudio) {
      this.previewMuted = true;
      video.muted = true;
    }
  }

  private async exitPreviewFullscreen(): Promise<void> {
    const doc = document as Document & { webkitExitFullscreen?: () => Promise<void> | void };
    if (document.fullscreenElement && document.exitFullscreen) {
      await document.exitFullscreen();
      this.previewIsFullscreen = false;
      return;
    }
    if (doc.webkitExitFullscreen) {
      await doc.webkitExitFullscreen();
      this.previewIsFullscreen = false;
    }
  }

  private async renderVideoBlob(format: VideoFormat): Promise<Blob | null> {
    if (!this.isWebCodecsVideoEncodingAvailable()) {
      this.logEncodingError('Mediabunny unavailable: VideoEncoder missing', {
        hasVideoEncoder: typeof globalThis.VideoEncoder !== 'undefined',
        hasVideoFrame: typeof globalThis.VideoFrame !== 'undefined',
      });
      this.setVideoStatus('error', 'Video encoder is not supported in this browser.');
      return null;
    }
    const blob = await this.renderVideoWithMediabunny(format);
    if (!blob) this.setVideoStatus('error', `${format.toUpperCase()} export unavailable in this browser.`);
    return blob;
  }

  private isWebCodecsVideoEncodingAvailable(): boolean {
    return typeof globalThis.VideoEncoder !== 'undefined' && typeof globalThis.VideoFrame !== 'undefined';
  }

  private async renderVideoWithMediabunny(format: VideoFormat): Promise<Blob | null> {
    if (!this.frames.length) {
      this.setVideoStatus('error', 'No frames available for preview/export.');
      return null;
    }

    const fps = this.videoFps;
    const targetWidth = this.videoWidth;
    const { width, height } = this.settings.Resolution;
    const size = this.getVideoSizeForWidth(targetWidth);
    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = width;
    sourceCanvas.height = height;
    const sourceCtx = sourceCanvas.getContext('2d');
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = size.width;
    outputCanvas.height = size.height;
    const outputCtx = outputCanvas.getContext('2d');
    if (!sourceCtx || !outputCtx) {
      this.setVideoStatus('error', 'Could not initialize export canvas.');
      return null;
    }
    sourceCtx.imageSmoothingEnabled = false;
    outputCtx.imageSmoothingEnabled = false;

    this.isExporting = true;
    this.exportProgress = 0;
    try {
      const codecs = this.getMediabunnyCodecCandidates(format);
      for (const codec of codecs) {
        this.setVideoStatus('info', `Encoding ${format.toUpperCase()} (${codec.toUpperCase()})...`);
        const blob = await this.tryRenderVideoWithMediabunnyCodec(format, codec, sourceCtx, outputCtx, sourceCanvas, outputCanvas, width, height, fps);
        if (blob) return blob;
        this.logEncodingError('Mediabunny codec attempt failed', { format, codec, width: size.width, height: size.height, fps });
      }
      this.logEncodingError('Mediabunny codec capability check failed', { format, width: size.width, height: size.height, fps });
      return null;
    } finally {
      this.isExporting = false;
      this.exportProgress = 0;
    }
  }

  private logEncodingError(message: string, error: unknown): void {
    console.error(`[video-export] ${message}`, error);
  }

  getMediabunnyFormats(): VideoFormat[] {
    return ['mp4', 'webm', 'mov', 'mkv'];
  }

  private createMediabunnyOutputFormat(format: VideoFormat): Mp4OutputFormat | WebMOutputFormat | MkvOutputFormat | MovOutputFormat {
    if (format === 'mp4') return new Mp4OutputFormat();
    if (format === 'webm') return new WebMOutputFormat();
    if (format === 'mov') return new MovOutputFormat();
    return new MkvOutputFormat();
  }

  private getMimeTypeForVideoFormat(format: VideoFormat): string {
    if (format === 'mp4') return 'video/mp4';
    if (format === 'webm') return 'video/webm';
    if (format === 'mov') return 'video/quicktime';
    return 'video/x-matroska';
  }

  private getMediabunnyCodecCandidates(format: VideoFormat): Array<'avc' | 'vp9' | 'vp8' | 'av1'> {
    return format === 'webm'
      ? ['vp9', 'vp8', 'av1']
      : format === 'mov'
      ? ['avc', 'av1']
      : format === 'mkv'
      ? ['avc', 'vp9', 'vp8', 'av1']
      : ['avc', 'av1'];
  }

  private async tryRenderVideoWithMediabunnyCodec(
    format: VideoFormat,
    codec: 'avc' | 'vp9' | 'vp8' | 'av1',
    sourceCtx: CanvasRenderingContext2D,
    outputCtx: CanvasRenderingContext2D,
    sourceCanvas: HTMLCanvasElement,
    outputCanvas: HTMLCanvasElement,
    width: number,
    height: number,
    fps: number,
  ): Promise<Blob | null> {
    const target = new BufferTarget();
    const output = new MediaOutput({
      format: this.createMediabunnyOutputFormat(format),
      target,
    });
    const videoSource = new CanvasSource(outputCanvas, {
      codec,
      bitrate: QUALITY_HIGH,
      latencyMode: 'quality',
      keyFrameInterval: 1,
    });
    output.addVideoTrack(videoSource);

    try {
      await output.start();
      const frameDurationS = 1 / fps;
      for (let i = 0; i < this.frames.length; i++) {
        this.exportProgress = Math.round(((i + 1) / this.frames.length) * 100);
        sourceCtx.fillStyle = '#000000';
        sourceCtx.fillRect(0, 0, width, height);
        const frame = this.frames[i];
        for (let r = 0; r < height; r++) for (let c = 0; c < width; c++) {
          sourceCtx.fillStyle = frame.pixels[r]?.[c] ?? '#000000';
          sourceCtx.fillRect(c, r, 1, 1);
        }
        outputCtx.drawImage(sourceCanvas, 0, 0, outputCanvas.width, outputCanvas.height);
        await videoSource.add(i * frameDurationS, frameDurationS);
      }

      await output.finalize();
      if (!target.buffer) return null;
      return new Blob([target.buffer], { type: this.getMimeTypeForVideoFormat(format) });
    } catch (error) {
      this.logEncodingError(`Mediabunny ${format.toUpperCase()} encode failed (${codec})`, error);
      try { await output.cancel(); } catch {}
      return null;
    }
  }

  private setVideoStatus(tone: VideoStatusTone, message: string): void {
    this.videoStatusTone = tone;
    this.videoStatusMessage = message;
  }

  private clearVideoStatus(): void {
    this.videoStatusTone = 'info';
    this.videoStatusMessage = '';
  }

  private setImageStatus(tone: VideoStatusTone, message: string): void {
    this.imageStatusTone = tone;
    this.imageStatusMessage = message;
  }

  private clearImageStatus(): void {
    this.imageStatusTone = 'info';
    this.imageStatusMessage = '';
  }

  private setGifStatus(tone: VideoStatusTone, message: string): void {
    this.gifStatusTone = tone;
    this.gifStatusMessage = message;
  }

  private clearGifStatus(): void {
    this.gifStatusTone = 'info';
    this.gifStatusMessage = '';
  }

  private findTransparentPaletteIndex(palette: number[][]): number {
    for (let i = 0; i < palette.length; i++) {
      const p = palette[i];
      if (p.length >= 4 && p[3] === 0) return i;
    }
    return -1;
  }

}
